import { strapiImageGetters } from './strapiImageGetters'
import type {
  StrapiArticleSummaryList,
  StrapiMagazineArticleCategory,
  StrapiMagazineArticle,
} from '~/composables/types/strapi/strapi-magazine'
import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'

const getId = (
  articleCategory: StrapiMagazineArticleCategory
): number | null => {
  return articleCategory?.id || null
}

const getSlug = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.slug || null
}

const getHeaderImage = (articleCategory: StrapiMagazineArticleCategory) => {
  return articleCategory?.header_image?.data || null
}

const getHeaderImageUrl = (
  articleCategory: StrapiMagazineArticleCategory,
  format = null
): string | null => {
  return (
    strapiImageGetters.getImageUrl(getHeaderImage(articleCategory), format) ||
    null
  )
}

const getTitle = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.title || null
}

const getIcon = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.icon || null
}

const getDescription = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.description || null
}

const getSeoTitle = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.seo?.metaTitle || null
}

const getSeoImageUrl = (
  articleCategory: StrapiMagazineArticleCategory,
  format = null
): string | null => {
  return (
    strapiImageGetters.getImageUrl(
      articleCategory?.seo?.metaImage?.data,
      format
    ) || null
  )
}

const getSeoMetaDescription = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.seo?.metaDescription || null
}

const getCreatedAt = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.createdAt || null
}

const getSummaryList = (
  articleCategory: StrapiMagazineArticleCategory
): StrapiArticleSummaryList => {
  return articleCategory?.summary_list || null
}

const getArticles = (
  articleCategory: StrapiMagazineArticleCategory | StrapiEventListPage | null
): StrapiMagazineArticle[] => {
  if (!articleCategory) return []

  // Handle both possible data structures
  const articles =
    (articleCategory as any)?.articles?.data ||
    (articleCategory as any)?.articles ||
    []
  return Array.isArray(articles) ? articles : []
}

const getLocale = (
  articleCategory: StrapiMagazineArticleCategory
): string | null => {
  return articleCategory?.locale ?? null
}

const getLocalizations = (
  articleCategory: StrapiMagazineArticleCategory
): StrapiMagazineArticleCategory['localizations'] => {
  return articleCategory?.localizations ?? null
}

export const articleCategoryGetters = {
  getId,
  getSlug,
  getHeaderImage,
  getHeaderImageUrl,
  getTitle,
  getIcon,
  getDescription,
  getSeoTitle,
  getSeoMetaDescription,
  getCreatedAt,
  getSummaryList,
  getSeoImageUrl,
  getArticles,
  getLocale,
  getLocalizations,
}
